import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { lecturers, LECTURER_IMG } from "@/data/team";
import { Avatar } from "@/components/Avatar";
import { Marquee } from "@/components/Marquee";
import { Seo } from "@/components/Seo";

const fade = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6 },
};

const pillars = [
  { t: "Praktičnost", d: "Soustředíme se výhradně na dovednosti, které mají reálné využití v každodenním životě." },
  { t: "Originalita", d: "Naše unikátní vzdělávací metody se vymykají standardním kurzům dostupným na trhu." },
  { t: "Profesionalita", d: "Naši lektoři jsou experti, kteří své obory znají skrz naskrz a dokáží znalosti efektivně předat." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-gavito-light">
      <Seo title="Proč my" description="Vzděláváme úplně jinak, než jste zvyklí. Poznejte tým lektorů GAVITO technic a náš přístup k praktickému vzdělávání." path="/o-nas" />
      {/* HERO */}
      <section className="pt-40 md:pt-48 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-4xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-gavito-green mb-6">Proč my</p>
            <h1 className="font-heading font-black text-5xl md:text-7xl tracking-tighter leading-[0.95] text-gavito-navy">
              Vzděláváme úplně jinak, než jste <span className="text-gavito-green">zvyklí.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl font-medium text-gavito-navy/70 max-w-2xl leading-relaxed">
              Naše kurzy se výrazně liší od běžné nabídky na trhu. Vytváříme interaktivní a poutavé
              vzdělávací zážitky, které vás skutečně posunou vpřed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* STATEMENT */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-white border border-gavito-navy/10 rounded-[2rem] p-10 md:p-16">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-gavito-green mb-5">Náš přístup</p>
            <p className="font-heading font-extrabold text-2xl md:text-4xl tracking-tight text-gavito-navy leading-tight max-w-4xl">
              Neplýtváme vaším časem na obecné teorie. V našich kurzech získáte konkrétní dovednosti,
              které <span className="text-gavito-green">okamžitě využijete v praxi.</span>
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-gavito-green mb-4">Na čem si zakládáme</p>
          <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-gavito-navy mb-12">Naše hodnoty</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <motion.div key={p.t} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white border border-gavito-navy/10 rounded-3xl p-10">
                <span className="font-heading font-black text-5xl text-gavito-green/30">0{i + 1}</span>
                <h3 className="font-heading font-bold text-2xl md:text-3xl text-gavito-navy mt-6">{p.t}</h3>
                <p className="mt-4 text-gavito-navy/65 leading-relaxed text-lg">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-gavito-navy py-7 md:py-9">
        <Marquee text="ZKUŠENÍ LEKTOŘI · PRAKTICKÉ DOVEDNOSTI · 2000+ ODUČENÝCH LIDÍ" className="text-3xl md:text-5xl text-gavito-green" />
      </section>

      {/* LECTURERS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-gavito-green mb-4">Náš tým</p>
          <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-gavito-navy mb-4">Lektoři, kteří vás posunou</h2>
          <p className="text-gavito-navy/60 mb-12 max-w-xl text-lg">
            V týmu máme řadu zkušených lektorů z praxe. Každý je expertem ve svém oboru.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {lecturers.map((l, i) => (
              <motion.div
                key={l.id}
                {...fade}
                transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
                data-testid={`lecturer-card-${l.id}`}
                className="bg-white border border-gavito-navy/10 rounded-3xl p-5 flex flex-col hover:shadow-[0_20px_50px_-30px_rgba(27,42,78,0.35)] transition-shadow"
              >
                <Avatar src={l.img ? LECTURER_IMG + l.img : null} name={l.name} initials={l.initials} className="mb-4" />
                <h3 className="font-heading font-bold text-lg text-gavito-navy text-center">{l.name}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-gavito-green rounded-[2.5rem] px-8 md:px-16 py-14 md:py-16 flex flex-col md:flex-row items-center justify-between gap-8">
            <h2 className="font-heading font-black text-3xl md:text-5xl tracking-tighter text-gavito-navy text-center md:text-left">
              Připraveni začít? Vyberte si kurz.
            </h2>
            <Link to="/kurzy" data-testid="about-cta-courses" className="inline-flex items-center gap-2 bg-gavito-navy text-white font-semibold px-8 py-4 rounded-full hover:bg-white hover:text-gavito-navy transition-colors shrink-0">
              Naše kurzy <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
